import { useEffect, useRef, useState } from 'react';

import { readPropertyValue } from '../targetResolvers.ts';
import type { EditPatch, EditableDescriptor } from '../types.ts';

import { ColorPicker } from './ColorPicker.tsx';

type TextPopupProps = {
	descriptor: EditableDescriptor;
	iframeDoc: Document;
	/** Trigger debounced serialization — does NOT write back to the DOM. */
	onRequestSerialize: () => void;
	brandColors?: string[] | null;
	onApplyEdit: (
		descriptor: EditableDescriptor,
		patch: Partial<EditPatch>,
	) => void;
};

export const TextPopup = ({
	descriptor,
	iframeDoc,
	onRequestSerialize,
	brandColors,
	onApplyEdit,
}: TextPopupProps) => {
	const elementRef = useRef<HTMLElement | null>(null);

	const element = iframeDoc.getElementById(descriptor.id);
	const currentTextColor =
		(element
			? readPropertyValue(element, descriptor.role, 'textColor')
			: null) || '#000000';

	const [textHex, setTextHex] = useState(currentTextColor);

	// Resync color when switching to a different element
	useEffect(() => {
		setTextHex(currentTextColor);
	}, [descriptor.id]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		const el = iframeDoc.getElementById(descriptor.id);
		if (!el) return;

		elementRef.current = el;

		// Capture original contenteditable before editor overwrites it.
		// Use sentinel '__none__' to distinguish "absent" from "present but empty".
		if (!el.dataset.bwEditorCe) {
			const raw = el.getAttribute('contenteditable');
			el.dataset.bwPrevCe = raw === null ? '__none__' : raw;
		}
		el.contentEditable = 'true';
		el.dataset.bwEditorCe = '1';
		el.focus();

		// The user is typing directly into the contentEditable element —
		// we only need to trigger serialization, NOT write innerText back,
		// which would reset the caret position.
		const handleInput = () => {
			onRequestSerialize();
		};

		el.addEventListener('input', handleInput);

		return () => {
			el.removeEventListener('input', handleInput);
			// Restore original contenteditable value
			const prev = el.dataset.bwPrevCe;
			if (prev === '__none__') {
				el.removeAttribute('contenteditable');
			} else {
				el.setAttribute('contenteditable', prev ?? '');
			}
			// Keep data-bw-editor-ce and data-bw-prev-ce — serialize cleanup handles them
			elementRef.current = null;
		};
	}, [descriptor, iframeDoc, onRequestSerialize]);

	return (
		<div className="flex flex-col gap-3">
			<ColorPicker
				label="Text Color"
				value={textHex}
				onChange={(hex) => {
					setTextHex(hex);
					onApplyEdit(descriptor, { textColor: hex });
				}}
				brandColors={brandColors}
			/>
		</div>
	);
};
